import { Suspense } from "react";
import { createRootRouteWithContext, createRoute, createRouter, isRedirect, lazyRouteComponent, Outlet, redirect } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { Shell } from "./components/Shell.tsx";
import { RouteError, Skeleton } from "./components/ui.tsx";
import { ApiError } from "./lib/api.ts";
import { CityProvider, sessionQuery } from "./lib/session.tsx";
import { SignInPage } from "./pages/SignIn.tsx";
import { OverviewPage } from "./pages/Overview.tsx";
import { DriversPage } from "./pages/Drivers.tsx";
import { TeamPage } from "./pages/Team.tsx";

const root = createRootRouteWithContext<{ queryClient: QueryClient }>()({ component: Outlet });

const signIn = createRoute({
  getParentRoute: () => root,
  path: "/entrar",
  validateSearch: (s: Record<string, unknown>) => ({ erro: typeof s.erro === "string" ? s.erro : undefined }),
  component: SignInPage,
});

const twoFactor = createRoute({ getParentRoute: () => root, path: "/entrar/verificar", component: lazyRouteComponent(() => import("./pages/TwoFactor.tsx"), "TwoFactorPage") });

/** Everything behind sign-in + TOTP. */
const app = createRoute({
  getParentRoute: () => root,
  id: "app",
  beforeLoad: async ({ context }) => {
    try {
      const s = await context.queryClient.ensureQueryData(sessionQuery);
      if (!s.twoFactor.verified) throw redirect({ to: "/entrar/verificar" });
    } catch (e) {
      if (isRedirect(e)) throw e;
      if (e instanceof ApiError && e.status === 401) throw redirect({ to: "/entrar", search: { erro: undefined } });
      if (e instanceof ApiError && e.code.startsWith("two_factor")) throw redirect({ to: "/entrar/verificar" });
      if (e instanceof ApiError) throw redirect({ to: "/entrar", search: { erro: e.code } });
      throw e;
    }
  },
  component: () => (
    <CityProvider>
      <Shell>
        <Suspense fallback={<div style={{ paddingTop: 24 }}><Skeleton h={200} /></div>}>
          <Outlet />
        </Suspense>
      </Shell>
    </CityProvider>
  ),
});

const overview = createRoute({ getParentRoute: () => app, path: "/", component: OverviewPage });

const drivers = createRoute({
  getParentRoute: () => app,
  path: "/cadastros",
  validateSearch: (s: Record<string, unknown>) => ({
    fila: s.fila === "resubmission" || s.fila === "documents" || s.fila === "expiring" || s.fila === "liveness" ? s.fila : ("pending" as "pending" | "resubmission" | "documents" | "expiring" | "liveness"),
    id: typeof s.id === "string" ? s.id : undefined,
  }),
  component: DriversPage,
});

export type TripSearch = {
  status: "all" | "active" | "completed" | "cancelled" | "incident"; period: "today" | "7d" | "30d"; q?: string; id?: string;
  /** Agendadas, Com pet, Cartão, Com cupom. */
  feature?: "scheduled" | "pet" | "card" | "cash" | "unpaid" | "promo";
};
const trips = createRoute({
  getParentRoute: () => app,
  path: "/corridas",
  validateSearch: (s: Record<string, unknown>): TripSearch => ({
    status: s.status === "active" || s.status === "completed" || s.status === "cancelled" || s.status === "incident" ? s.status : "all",
    period: s.period === "7d" || s.period === "30d" ? s.period : "today",
    q: typeof s.q === "string" && s.q ? s.q : undefined,
    id: typeof s.id === "string" ? s.id : undefined,
    feature: (["scheduled", "pet", "card", "cash", "unpaid", "promo"] as const).find((f) => f === s.feature),
  }),
  component: lazyRouteComponent(() => import("./pages/Trips.tsx"), "TripsPage"),
});

const liveTrips = createRoute({
  getParentRoute: () => app,
  path: "/ao-vivo/corridas",
  validateSearch: (s: Record<string, unknown>) => ({ id: typeof s.id === "string" ? s.id : undefined }),
  component: lazyRouteComponent(() => import("./pages/LiveTrips.tsx"), "LiveTripsPage"),
});
const liveDrivers = createRoute({
  getParentRoute: () => app,
  path: "/ao-vivo/motoristas",
  validateSearch: (s: Record<string, unknown>): { id?: string } => ({ id: typeof s.id === "string" && s.id ? s.id : undefined }),
  component: lazyRouteComponent(() => import("./pages/LiveDrivers.tsx"), "LiveDriversPage"),
});
export type DispatchSearch = {
  aba: "analise" | "ao-vivo";
  periodo: "1h" | "today" | "24h" | "7d" | "30d";
  /** Comma-separated status groups (ativas, concluidas, canceladas, sem, pix). */
  status?: string;
  /** Comma-separated categories. */
  cat?: string;
  tipo: "all" | "ride" | "delivery";
  /** Comma-separated problem presets. */
  flags?: string;
  /** H3 cell of the pickup zone. */
  zona?: string;
  motorista?: string;
  q?: string;
  ordem: "recent" | "slowest" | "offers" | "rings" | "late";
  res: 7 | 8 | 9;
  metrica: "requests" | "no_driver" | "match" | "late";
};
const oneOf = <T extends string>(v: unknown, all: readonly T[], fallback: T): T => (all.includes(v as T) ? (v as T) : fallback);
const dispatch = createRoute({
  getParentRoute: () => app,
  path: "/despacho",
  validateSearch: (s: Record<string, unknown>): DispatchSearch => ({
    aba: oneOf(s.aba, ["analise", "ao-vivo"], "analise"),
    periodo: oneOf(s.periodo, ["1h", "today", "24h", "7d", "30d"], "24h"),
    status: text(s.status),
    cat: text(s.cat),
    tipo: oneOf(s.tipo, ["all", "ride", "delivery"], "all"),
    flags: text(s.flags),
    zona: text(s.zona),
    motorista: text(s.motorista),
    q: text(s.q),
    ordem: oneOf(s.ordem, ["recent", "slowest", "offers", "rings", "late"], "recent"),
    res: s.res === 7 || s.res === "7" ? 7 : s.res === 9 || s.res === "9" ? 9 : 8,
    metrica: oneOf(s.metrica, ["requests", "no_driver", "match", "late"], "requests"),
  }),
  component: lazyRouteComponent(() => import("./pages/Dispatch.tsx"), "DispatchPage"),
});
const dispatchTrip = createRoute({
  getParentRoute: () => app,
  path: "/despacho/$id",
  component: lazyRouteComponent(() => import("./pages/DispatchTrip.tsx"), "DispatchTripPage"),
});

const team = createRoute({ getParentRoute: () => app, path: "/equipe", component: TeamPage });

export type AuditSearch = { ator?: string; acao?: string; entidade?: string; alvo?: string; de?: string; ate?: string };
const day = (v: unknown) => (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined);
const text = (v: unknown) => (typeof v === "string" && v ? v : undefined);
const audit = createRoute({
  getParentRoute: () => app,
  path: "/equipe/auditoria",
  validateSearch: (s: Record<string, unknown>): AuditSearch => ({ ator: text(s.ator), acao: text(s.acao), entidade: text(s.entidade), alvo: text(s.alvo), de: day(s.de), ate: day(s.ate) }),
  component: lazyRouteComponent(() => import("./pages/Audit.tsx"), "AuditPage"),
});

export type SafetySearch = {
  /** Ocorrências (SOS, relatos) or Alertas de rota; a link with `tipo` means the alerts. */
  aba: "ocorrencias" | "alertas";
  tipo: "all" | "open" | "deviation" | "auto_share" | "arrival" | "recipient"; periodo: "today" | "7d" | "30d";
  situacao: "active" | "open" | "acknowledged" | "resolved" | "all"; natureza: "all" | "sos" | "report"; id?: string;
};
const safety = createRoute({
  getParentRoute: () => app,
  path: "/seguranca",
  validateSearch: (s: Record<string, unknown>): SafetySearch => ({
    aba: s.aba === "alertas" || s.aba === "ocorrencias" ? s.aba : s.tipo ? "alertas" : "ocorrencias",
    situacao: oneOf(s.situacao, ["active", "open", "acknowledged", "resolved", "all"], "active"),
    natureza: oneOf(s.natureza, ["all", "sos", "report"], "all"),
    id: text(s.id),
    tipo: s.tipo === "open" || s.tipo === "deviation" || s.tipo === "auto_share" || s.tipo === "arrival" || s.tipo === "recipient" ? s.tipo : "all",
    periodo: s.periodo === "today" || s.periodo === "30d" ? s.periodo : "7d",
  }),
  component: lazyRouteComponent(() => import("./pages/Safety.tsx"), "SafetyPage"),
});

export type FinanceSearch = { periodo: "7d" | "30d" | "12m"; aba: "resumo" | "conciliacao"; escopo: "cidade" | "todas" };
const finance = createRoute({
  getParentRoute: () => app,
  path: "/financeiro",
  validateSearch: (s: Record<string, unknown>): FinanceSearch => ({
    periodo: s.periodo === "7d" || s.periodo === "30d" ? s.periodo : "12m",
    aba: s.aba === "conciliacao" ? "conciliacao" : "resumo",
    escopo: s.escopo === "todas" ? "todas" : "cidade",
  }),
  component: lazyRouteComponent(() => import("./pages/Finance.tsx"), "FinancePage"),
});

export type PayoutSearch = { lote?: string; filtro: "all" | "failed" | "held" | "paid" | "pending" };
const payouts = createRoute({
  getParentRoute: () => app,
  path: "/repasses",
  validateSearch: (s: Record<string, unknown>): PayoutSearch => ({
    lote: typeof s.lote === "string" && s.lote ? s.lote : undefined,
    filtro: s.filtro === "failed" || s.filtro === "held" || s.filtro === "paid" || s.filtro === "pending" ? s.filtro : "all",
  }),
  component: lazyRouteComponent(() => import("./pages/Payouts.tsx"), "PayoutsPage"),
});

export type SupportSearch = {
  status: "active" | "all" | "open" | "waiting_staff" | "waiting_user" | "closed"; app: "all" | "rider" | "driver"; q?: string; id?: string;
  /** Topic id ("cobranca", "seguranca", …). */
  assunto?: string;
  /** "Minhas": assigned to me. */
  minhas?: boolean;
};
const support = createRoute({
  getParentRoute: () => app,
  path: "/suporte",
  validateSearch: (s: Record<string, unknown>): SupportSearch => ({
    status: s.status === "all" || s.status === "open" || s.status === "waiting_staff" || s.status === "waiting_user" || s.status === "closed" ? s.status : "active",
    app: s.app === "rider" || s.app === "driver" ? s.app : "all",
    q: typeof s.q === "string" && s.q ? s.q : undefined,
    id: typeof s.id === "string" && s.id ? s.id : undefined,
    assunto: typeof s.assunto === "string" && s.assunto ? s.assunto : undefined,
    minhas: s.minhas === true || s.minhas === "true" ? true : undefined,
  }),
  component: lazyRouteComponent(() => import("./pages/Support.tsx"), "SupportPage"),
});

export type RosterSearch = { status: "all" | "active" | "online" | "suspended"; q?: string; id?: string };
const roster = createRoute({
  getParentRoute: () => app,
  path: "/motoristas",
  validateSearch: (s: Record<string, unknown>): RosterSearch => ({
    status: s.status === "active" || s.status === "online" || s.status === "suspended" ? s.status : "all",
    q: typeof s.q === "string" && s.q ? s.q : undefined,
    id: typeof s.id === "string" && s.id ? s.id : undefined,
  }),
  component: lazyRouteComponent(() => import("./pages/Roster.tsx"), "RosterPage"),
});

export type RiderSearch = { status: "all" | "blocked" | "new" | "issues"; q?: string; id?: string };
const riders = createRoute({
  getParentRoute: () => app,
  path: "/passageiros",
  validateSearch: (s: Record<string, unknown>): RiderSearch => ({
    status: s.status === "blocked" || s.status === "new" || s.status === "issues" ? s.status : "all",
    q: typeof s.q === "string" && s.q ? s.q : undefined,
    id: typeof s.id === "string" && s.id ? s.id : undefined,
  }),
  component: lazyRouteComponent(() => import("./pages/Riders.tsx"), "RidersPage"),
});

export type SettingsTab = "tipos" | "tarifas" | "dinamica" | "pagamentos" | "operacao";
const settings = createRoute({
  getParentRoute: () => app,
  path: "/configuracoes",
  validateSearch: (s: Record<string, unknown>): { aba: SettingsTab } => ({
    aba: s.aba === "tarifas" || s.aba === "dinamica" || s.aba === "pagamentos" || s.aba === "operacao" ? s.aba : "tipos",
  }),
  component: lazyRouteComponent(() => import("./pages/Settings.tsx"), "SettingsPage"),
});

const promos = createRoute({ getParentRoute: () => app, path: "/cupons", component: lazyRouteComponent(() => import("./pages/Promos.tsx"), "PromosPage") });
const riderDeletions = createRoute({
  getParentRoute: () => app,
  path: "/passageiros/exclusoes",
  component: lazyRouteComponent(() => import("./pages/Deletions.tsx"), "RiderDeletionsPage"),
});
const driverDeletions = createRoute({
  getParentRoute: () => app,
  path: "/motoristas/exclusoes",
  component: lazyRouteComponent(() => import("./pages/Deletions.tsx"), "DriverDeletionsPage"),
});
const citiesPage = createRoute({ getParentRoute: () => app, path: "/cidades", component: lazyRouteComponent(() => import("./pages/Cities.tsx"), "CitiesPage") });

const notifications = createRoute({ getParentRoute: () => app, path: "/notificacoes", component: lazyRouteComponent(() => import("./pages/Notifications.tsx"), "NotificationsPage") });

const routeTree = root.addChildren([
  signIn, twoFactor,
  app.addChildren([overview, drivers, roster, riders, trips, liveTrips, liveDrivers, dispatch, dispatchTrip, safety, team, audit, finance, payouts, support, settings, citiesPage, notifications,
    promos, riderDeletions, driverDeletions]),
]);

export function makeRouter(queryClient: QueryClient) {
  // A screen that throws shows the error and a retry inside the shell instead of a blank page.
  return createRouter({ routeTree, context: { queryClient }, defaultPreload: "intent", defaultErrorComponent: RouteError });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof makeRouter>;
  }
}
